import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Shield, AlertTriangle, AlertCircle, CheckCircle2, Clock, User, Eye, 
  RefreshCw, Filter, Search, XCircle, Activity, TrendingUp, Users
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const RISK_COLORS = {
  LOW: 'bg-blue-100 text-blue-800 border-blue-200',
  MEDIUM: 'bg-amber-100 text-amber-800 border-amber-200',
  HIGH: 'bg-orange-100 text-orange-800 border-orange-200',
  CRITICAL: 'bg-red-100 text-red-800 border-red-200'
};

const STATUS_COLORS = {
  NEW: 'bg-red-100 text-red-800',
  REVIEWING: 'bg-amber-100 text-amber-800',
  RESOLVED: 'bg-green-100 text-green-800',
  FALSE_POSITIVE: 'bg-gray-100 text-gray-800',
  ESCALATED: 'bg-purple-100 text-purple-800'
};

const DETECTION_ICONS = {
  FORBIDDEN_ACCESS: AlertCircle,
  ROLE_ESCALATION_ATTEMPT: TrendingUp,
  CROSS_SCOPE_ACCESS: Users,
  EXCESSIVE_EXPORTS: Activity,
  UNUSUAL_ACTIVITY_SPIKE: TrendingUp,
  AFTER_HOURS_SENSITIVE: Clock,
  BRUTE_FORCE_ATTEMPT: AlertTriangle,
  SUPER_ADMIN_PROBE: Shield,
  MASS_DATA_ACCESS: Activity,
  SUSPICIOUS_PATTERN: Eye
};

export default function SecurityAlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolveStatus, setResolveStatus] = useState('RESOLVED');
  const [filters, setFilters] = useState({
    status: '',
    riskLevel: '',
    detectionType: ''
  });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 25;

  const fetchAlerts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('pageSize', pageSize);
      if (filters.status) params.append('status', filters.status);
      if (filters.riskLevel) params.append('riskLevel', filters.riskLevel);
      if (filters.detectionType) params.append('detectionType', filters.detectionType);

      const res = await api.get(`/security/alerts?${params.toString()}`);
      setAlerts(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error('Failed to load alerts', e);
      toast.error('Failed to load security alerts');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.get('/security/alerts/summary');
      setSummary(res.data.data);
    } catch (e) {
      console.error('Failed to load summary', e);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    fetchSummary();
  }, [fetchAlerts, fetchSummary]);

  const handleReview = async (alertId) => {
    try {
      await api.post(`/security/alerts/${alertId}/review`);
      toast.success('Alert marked as reviewing');
      fetchAlerts();
      fetchSummary();
    } catch (e) {
      toast.error('Failed to update alert');
    }
  };

  const handleResolve = async () => {
    if (!selectedAlert) return;
    try {
      await api.post(`/security/alerts/${selectedAlert.id}/resolve`, {
        status: resolveStatus,
        notes: resolveNotes
      });
      toast.success('Alert resolved');
      setShowResolveDialog(false);
      setResolveNotes('');
      fetchAlerts();
      fetchSummary();
    } catch (e) {
      toast.error('Failed to resolve alert');
    }
  };

  const openDetail = (alert) => {
    setSelectedAlert(alert);
    setShowDetailDialog(true);
  };

  const openResolve = (alert) => {
    setSelectedAlert(alert);
    setShowResolveDialog(true);
  };

  const getRiskIcon = (level) => {
    switch (level) {
      case 'CRITICAL': return <AlertTriangle className="h-5 w-5 text-red-600" />;
      case 'HIGH': return <AlertCircle className="h-5 w-5 text-orange-600" />;
      case 'MEDIUM': return <AlertCircle className="h-5 w-5 text-amber-600" />;
      default: return <AlertCircle className="h-5 w-5 text-blue-600" />;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="security-alerts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            Security Alerts
          </h1>
          <p className="text-muted-foreground mt-1">AI-powered misuse detection and security monitoring</p>
        </div>
        <Button onClick={() => { fetchAlerts(); fetchSummary(); }} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="border-red-200 bg-red-50/50">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Critical</p>
                  <p className="text-2xl font-bold text-red-600">{summary.by_risk_level?.CRITICAL || 0}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-orange-200 bg-orange-50/50">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">High</p>
                  <p className="text-2xl font-bold text-orange-600">{summary.by_risk_level?.HIGH || 0}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-orange-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Medium</p>
                  <p className="text-2xl font-bold text-amber-600">{summary.by_risk_level?.MEDIUM || 0}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">New Alerts</p>
                  <p className="text-2xl font-bold">{summary.new_alerts_count || 0}</p>
                </div>
                <Clock className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Risk Score</p>
                  <p className="text-2xl font-bold">{summary.total_risk_score || 0}</p>
                </div>
                <Activity className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Resolved</p>
                  <p className="text-2xl font-bold text-green-600">{summary.by_status?.RESOLVED || 0}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" /> Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Select value={filters.status || 'all'} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
            <SelectTrigger className="w-[160px]" data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="NEW">New</SelectItem>
              <SelectItem value="REVIEWING">Reviewing</SelectItem>
              <SelectItem value="RESOLVED">Resolved</SelectItem>
              <SelectItem value="FALSE_POSITIVE">False Positive</SelectItem>
              <SelectItem value="ESCALATED">Escalated</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.riskLevel || 'all'} onValueChange={(v) => setFilters({ ...filters, riskLevel: v === 'all' ? '' : v })}>
            <SelectTrigger className="w-[160px]" data-testid="filter-risk">
              <SelectValue placeholder="Risk Level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Levels</SelectItem>
              <SelectItem value="CRITICAL">Critical</SelectItem>
              <SelectItem value="HIGH">High</SelectItem>
              <SelectItem value="MEDIUM">Medium</SelectItem>
              <SelectItem value="LOW">Low</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.detectionType || 'all'} onValueChange={(v) => setFilters({ ...filters, detectionType: v === 'all' ? '' : v })}>
            <SelectTrigger className="w-[200px]" data-testid="filter-type">
              <SelectValue placeholder="Detection Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="FORBIDDEN_ACCESS">Forbidden Access</SelectItem>
              <SelectItem value="ROLE_ESCALATION_ATTEMPT">Role Escalation</SelectItem>
              <SelectItem value="CROSS_SCOPE_ACCESS">Cross-Scope Access</SelectItem>
              <SelectItem value="BRUTE_FORCE_ATTEMPT">Brute Force</SelectItem>
              <SelectItem value="SUPER_ADMIN_PROBE">Super Admin Probe</SelectItem>
              <SelectItem value="AFTER_HOURS_SENSITIVE">After-Hours Activity</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={() => setFilters({ status: '', riskLevel: '', detectionType: '' })}>
            Clear Filters
          </Button>
        </CardContent>
      </Card>

      {/* Alerts Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : alerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Shield className="h-12 w-12 text-green-500 mb-4" />
              <p className="text-lg font-medium">No Security Alerts</p>
              <p className="text-sm text-muted-foreground">All clear! No suspicious activity detected.</p>
            </div>
          ) : (
            <div className="divide-y">
              {alerts.map((alert) => {
                const DetectionIcon = DETECTION_ICONS[alert.detection_type] || AlertCircle;
                return (
                  <div
                    key={alert.id}
                    className="flex items-center justify-between p-4 hover:bg-muted/50 cursor-pointer"
                    onClick={() => openDetail(alert)}
                    data-testid={`alert-row-${alert.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg ${RISK_COLORS[alert.risk_level]}`}>
                        {getRiskIcon(alert.risk_level)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{alert.summary}</p>
                          <Badge className={STATUS_COLORS[alert.status]}>{alert.status}</Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <DetectionIcon className="h-3 w-3" />
                            {alert.detection_type.replace(/_/g, ' ')}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {alert.actor_email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {format(parseISO(alert.created_at), 'MMM d, HH:mm')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {alert.status === 'NEW' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); handleReview(alert.id); }}
                          data-testid={`review-btn-${alert.id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" /> Review
                        </Button>
                      )}
                      {['NEW', 'REVIEWING'].includes(alert.status) && (
                        <Button
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); openResolve(alert); }}
                          data-testid={`resolve-btn-${alert.id}`}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-1" /> Resolve
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, total)} of {total} alerts
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedAlert && getRiskIcon(selectedAlert.risk_level)}
              Alert Details
            </DialogTitle>
          </DialogHeader>
          {selectedAlert && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Detection Type</p>
                  <p className="font-medium">{selectedAlert.detection_type.replace(/_/g, ' ')}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Risk Level</p>
                  <Badge className={RISK_COLORS[selectedAlert.risk_level]}>{selectedAlert.risk_level}</Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <Badge className={STATUS_COLORS[selectedAlert.status]}>{selectedAlert.status}</Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Risk Score</p>
                  <p className="font-medium">{selectedAlert.risk_score}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Summary</p>
                <p className="font-medium">{selectedAlert.summary}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Actor</p>
                  <p className="font-medium">{selectedAlert.actor_email}</p>
                  <p className="text-xs text-muted-foreground">Role: {selectedAlert.actor_role}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Created</p>
                  <p className="font-medium">{format(parseISO(selectedAlert.created_at), 'PPpp')}</p>
                </div>
              </div>
              {selectedAlert.evidence && selectedAlert.evidence.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Evidence</p>
                  <pre className="bg-muted p-3 rounded-lg text-xs overflow-auto max-h-40">
                    {JSON.stringify(selectedAlert.evidence, null, 2)}
                  </pre>
                </div>
              )}
              {selectedAlert.reviewed_by && (
                <div className="border-t pt-4">
                  <p className="text-xs text-muted-foreground">Reviewed by</p>
                  <p className="font-medium">{selectedAlert.reviewer_name}</p>
                  {selectedAlert.resolution_notes && (
                    <p className="text-sm mt-1">{selectedAlert.resolution_notes}</p>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve Dialog */}
      <Dialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Alert</DialogTitle>
            <DialogDescription>Choose a resolution status and add notes if needed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Resolution Status</label>
              <Select value={resolveStatus} onValueChange={setResolveStatus}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="RESOLVED">Resolved - Issue addressed</SelectItem>
                  <SelectItem value="FALSE_POSITIVE">False Positive - Not a real threat</SelectItem>
                  <SelectItem value="ESCALATED">Escalated - Needs further investigation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Notes</label>
              <Textarea
                className="mt-1"
                placeholder="Add resolution notes..."
                value={resolveNotes}
                onChange={(e) => setResolveNotes(e.target.value)}
                data-testid="resolve-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolveDialog(false)}>Cancel</Button>
            <Button onClick={handleResolve} data-testid="confirm-resolve-btn">Confirm Resolution</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
